import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { cn } from '@command-center/ui-system';

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export function display(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(display).filter(Boolean).join(', ');
  if (typeof value === 'object') { const record = value as Record<string, unknown>; return display(record.name ?? record.label ?? record.id ?? ''); }
  return String(value);
}

export function linkedIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map(item => typeof item === 'string' ? item : typeof item === 'object' && item !== null ? String((item as { id?: unknown }).id ?? '') : '').filter(id => id.startsWith('rec'));
}

export function formatDate(value: unknown): string {
  const text = display(value);
  if (!text) return '';
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function projectCategories(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(display).filter(Boolean);
  return display(value).split(',').map(item => item.trim()).filter(Boolean);
}

const projectCategoryClasses: Record<string, string> = {
  noise: 'border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300',
  emmission: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  energy: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  heath: 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300',
  health: 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300',
  'health risk': 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300',
};

export function ProjectCategoryBadge({ category }: { category: string }) {
  const normalized = category.trim().toLowerCase();
  const label = normalized === 'heath' ? 'Health' : category;
  return <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium', projectCategoryClasses[normalized] ?? 'border-border bg-muted text-muted-foreground')}>{label}</span>;
}

export function relativeTime(value: unknown): string {
  const text = display(value);
  if (!text) return '';
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h ago`;
  if (seconds < 30 * 86_400) return `${Math.round(seconds / 86_400)}d ago`;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatBytes(value: unknown): string {
  const size = Number(display(value));
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Status tones
// ---------------------------------------------------------------------------

const toneClasses: Record<string, string> = {
  positive: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  caution: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
  danger: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30',
  info: 'bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/30',
  neutral: 'bg-muted text-muted-foreground border-border',
  brand: 'bg-primary/10 text-primary border-primary/30',
};

const valueTones: Record<string, keyof typeof toneClasses> = {
  'On Track': 'positive', 'Attention Needed': 'caution', 'At Risk': 'danger', Blocked: 'danger',
  Active: 'positive', 'On Hold': 'caution', Completed: 'info', Cancelled: 'neutral', Archived: 'neutral',
  Uploaded: 'neutral', Processing: 'info', 'Needs Review': 'caution', Reviewed: 'positive', Failed: 'danger', Superseded: 'neutral',
  'Not Started': 'neutral', Queued: 'info', 'Not Reviewed': 'caution', 'In Review': 'info', 'Changes Requested': 'caution',
  Urgent: 'danger', High: 'caution', Normal: 'neutral', Low: 'neutral',
  Intake: 'info', 'Information Gathering': 'info', 'Technical Analysis': 'brand', Drafting: 'brand', 'Internal Review': 'caution', 'Client Review': 'caution', 'Agency Review': 'caution', Revision: 'caution', Final: 'positive', Closeout: 'neutral',
};

export function StatusPill({ value, className }: { value: unknown; className?: string }) {
  const text = display(value);
  if (!text) return <span className="text-muted-foreground">—</span>;
  return <span className={cn('inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium', toneClasses[valueTones[text] ?? 'neutral'], className)}>{text}</span>;
}

export function ConfidencePill({ value }: { value: number | null | undefined }) {
  if (value === null || value === undefined) return null;
  const pct = Math.round(value * 100);
  const tone = pct >= 80 ? 'positive' : pct >= 50 ? 'caution' : 'danger';
  return <span className={cn('inline-flex items-center rounded-full border px-1.5 py-0.5 text-[11px] font-medium tabular-nums', toneClasses[tone])}>{pct}%</span>;
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

interface Toast { id: number; message: string; tone: 'success' | 'error' | 'info'; }
const ToastContext = createContext<{ toast(message: string, tone?: Toast['tone']): void } | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toast = useCallback((message: string, tone: Toast['tone'] = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(current => [...current.slice(-3), { id, message, tone }]);
    setTimeout(() => setToasts(current => current.filter(item => item.id !== id)), tone === 'error' ? 8000 : 4000);
  }, []);
  const value = useMemo(() => ({ toast }), [toast]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-80 flex-col gap-2">
        {toasts.map(item => (
          <div key={item.id} role="status" className={cn('pointer-events-auto rounded-lg border p-3 text-sm shadow-lg backdrop-blur', item.tone === 'error' ? 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300' : item.tone === 'info' ? 'border-sky-500/40 bg-background text-foreground' : 'border-emerald-500/40 bg-background text-foreground')}>
            {item.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() { const value = useContext(ToastContext); if (!value) throw new Error('ToastProvider missing.'); return value; }

// ---------------------------------------------------------------------------
// Starred + recent projects (local, per-browser)
// ---------------------------------------------------------------------------

function readList(key: string): Array<{ id: string; label: string }> {
  try { const raw = localStorage.getItem(key); const value = raw ? JSON.parse(raw) as Array<{ id: string; label: string }> : []; return Array.isArray(value) ? value : []; } catch { return []; }
}

export const stars = {
  key: 'ecorp-starred-projects',
  event: 'ecorp:starred-projects-changed',
  list(): Array<{ id: string; label: string }> { return readList(this.key); },
  has(id: string): boolean { return this.list().some(item => item.id === id); },
  toggle(id: string, label: string): boolean {
    const current = this.list();
    const exists = current.some(item => item.id === id);
    const next = exists ? current.filter(item => item.id !== id) : [...current, { id, label }];
    localStorage.setItem(this.key, JSON.stringify(next.slice(0, 50)));
    window.dispatchEvent(new Event(this.event));
    return !exists;
  },
};

export const recents = {
  key: 'ecorp-recent-projects',
  list(): Array<{ id: string; label: string }> { return readList(this.key); },
  push(id: string, label: string): void {
    const next = [{ id, label }, ...this.list().filter(item => item.id !== id)].slice(0, 10);
    localStorage.setItem(this.key, JSON.stringify(next));
  },
};

// ---------------------------------------------------------------------------
// Small shared UI bits
// ---------------------------------------------------------------------------

export function SectionHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-2 border-b pb-2">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </div>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed p-10 text-center">
      <p className="font-medium text-muted-foreground">{title}</p>
      {hint && <p className="mt-1 text-sm text-muted-foreground/80">{hint}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function Skeletons({ count = 6, className }: { count?: number; className?: string }) {
  return <div className={cn('grid gap-3 sm:grid-cols-2 xl:grid-cols-3', className)}>{Array.from({ length: count }, (_, index) => <div key={index} className="h-44 animate-pulse rounded-xl bg-muted" />)}</div>;
}

export function ErrorNotice({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
      <span>{message}</span>
      {onRetry && <button className="rounded-md border border-destructive/40 px-3 py-1 font-medium hover:bg-destructive/10" onClick={onRetry}>Retry</button>}
    </div>
  );
}
